// The shape `m8t platform status --output json` emits, and the one line of the
// pretty output that states versions.
//
// EXTRACTED SO IT CAN BE TESTED. The command itself does real Azure I/O —
// credential, storage discovery, a channel fetch, a container-app read — so it
// has never had a unit test, and the JSON shape was consequently unasserted.
// That is not academic: the finish-line smoke was written against a
// `platformVersion` key this command has never emitted, so its stamp leg could
// only ever fail. A shape nothing asserts is a shape callers guess at.
import type { InstalledStamp } from "@m8t-stack/platform-release";
import type { StampReadResult } from "./platform-stamp.js";
import type { StatusRow } from "./platform-converge.js";

/**
 * How the stamp read went, flattened for the wire.
 *
 * "absent" and "unreadable" are DIFFERENT ANSWERS and must not collapse.
 * `readStamp` returns null for both, which is why this reads
 * `readStampOutcome` instead: an install whose managed identity briefly lacked
 * the data-plane role is not an unstamped install, and reporting it as one sends
 * the reader looking for a missing install rather than a missing role.
 */
export type StampRead = "explicit" | "absent" | "unreadable";

export interface PlatformStatusJson {
  kind: "platform-status";
  gateway: string;
  /** What the CHANNEL currently offers. */
  targetVersion: string;
  /** What the install RECORDS it is running. null when there is no readable stamp. */
  installedVersion: string | null;
  /** Whether a stamp was actually read — see StampRead. */
  stampRead: StampRead;
  /** True ONLY for a stamp that was genuinely read. An unreadable stamp is not "unmanaged". */
  managed: boolean;
  rows: StatusRow[];
}

/** The stamp itself, or null when it was absent or unreadable. */
export function stampOf(outcome: StampReadResult): InstalledStamp | null {
  return outcome.source === "explicit" ? outcome.stamp : null;
}

export function buildStatusJson(args: {
  resourceGroup: string;
  targetVersion: string;
  outcome: StampReadResult;
  rows: StatusRow[];
}): PlatformStatusJson {
  const stamp = stampOf(args.outcome);
  return {
    kind: "platform-status",
    gateway: args.resourceGroup,
    targetVersion: args.targetVersion,
    // Emitted as null rather than omitted, always. A consumer can then tell "no
    // stamp" from "this CLI is too old to say", which an absent key cannot.
    installedVersion: stamp?.platformVersion ?? null,
    stampRead: args.outcome.source,
    managed: args.outcome.source === "explicit",
    rows: args.rows,
  };
}

/**
 * The versions line of the pretty output.
 *
 * BOTH numbers, not just the target. "target: platform-v0.7.8" alone reads as a
 * statement about the install when it is a statement about the channel, and the
 * difference between the two is the entire question anyone runs this command to
 * answer.
 */
export function statusVersionsLine(args: { targetVersion: string; outcome: StampReadResult }): string {
  const stamp = stampOf(args.outcome);
  const suffix =
    args.outcome.source === "absent"
      ? "  (unmanaged — no stamp found)"
      : args.outcome.source === "unreadable"
        ? "  (stamp UNREADABLE — not the same as unstamped; check the identity's data-plane access)"
        : "";
  return `installed: ${stamp?.platformVersion ?? "(none)"}    target: ${args.targetVersion}${suffix}`;
}
